"use client";

import {
  ArrowLeftIcon,
  CreditCardIcon,
  ShoppingCartIcon,
  X,
} from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useWesubCart } from "@/contexts/provider/wesub-cart-provider";
import { Spinner } from "@/components/ui/spinner";
import { CartItemRow } from "@/components/shop/cart-item";
import { SafeUpIcon } from "@/svg/club/safe-up-icon";
import { Button } from "../ui/button";
import { useContext, useState } from "react";
import { UserContext } from "@/contexts/user-context";
import { createWesubOrder } from "@/service/shop/wesub-create-order";
import { ClientTypeSwitch } from "@/components/shop/product-detail/client-type-switch";
import { PromoCodeBanner } from "@/components/shop/promo-code-banner";
import { useRouter } from "@bprogress/next";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";

// ─── Cart Drawer ───────────────────────────────────────────────────────────────

export function CartDrawer() {
  const { isOpen, closeCart, cart, isLoading } = useWesubCart();
  const { email } = useContext(UserContext) as { email?: string };
  const { logView } = useContext(ResourceContext);
  const router = useRouter();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isCompany, setIsCompany] = useState(false);

  async function handleCheckout() {
    setIsCheckingOut(true);
    setCheckoutError(null);
    try {
      const cartItems = cart?.items ?? [];
      const orderItems: WesubOrderItem[] = cartItems.map((ci) => ({
        clientProductId: ci.product.clientProductId,
        quantity: ci.quantity,
        sellPrice: ci.product.sellPrice,
      }));

      const { redirectUrl, rentUPOrderId } = await createWesubOrder(
        orderItems,
        {
          email: email ?? "",
        },
        !isCompany,
      );
      document.cookie = `wesub_pending_order=${rentUPOrderId}; path=/; max-age=3600; SameSite=Lax`;
      logEvent("club_wesub_redirect_to_checkout", logView, {
        order_id: rentUPOrderId,
        is_company: isCompany,
        total_quantity: totalQuantity,
        total_brutto: totalBrutto,
        total_netto: totalNetto,
        products: orderItems,
      });
      router.push(redirectUrl);
    } catch {
      setCheckoutError("Nie udało się utworzyć zamówienia. Spróbuj ponownie.");
    } finally {
      setIsCheckingOut(false);
    }
  }

  const cartItems = cart?.items ?? [];

  const finalCartItems = cartItems.map((ci) => ({
    product: ci.product,
    quantity: ci.quantity,
  }));

  const totalQuantity = finalCartItems.reduce(
    (sum, { quantity }) => sum + quantity,
    0,
  );

  // B2B: b2bMonthPrice is netto → add VAT on top
  // B2C: b2cMonthPrice is brutto → back-calculate netto
  // Fallback: monthPrice treated as netto
  const totals = finalCartItems.reduce(
    (acc, { product, quantity }) => {
      if (isCompany) {
        const netto =
          (product.custom?.b2bMonthPrice ??
            product.monthPrice ??
            product.sellPrice) * quantity;
        const vat = netto * (product.vat ?? 0);
        return { netto: acc.netto + netto, vat: acc.vat + vat };
      } else {
        const brutto =
          (product.custom?.b2cMonthPrice ??
            product.monthPrice ??
            product.sellPrice) * quantity;
        const vat = brutto - brutto / (1 + (product.vat ?? 0));
        return { netto: acc.netto + brutto - vat, vat: acc.vat + vat };
      }
    },
    { netto: 0, vat: 0 },
  );

  const totalNetto = totals.netto;
  const totalVat = totals.vat;
  const totalBrutto = totalNetto + totalVat;
  const displayPrice = isCompany ? totalNetto : totalBrutto;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closeCart();
      }}
      direction="right"
    >
      <DrawerContent className="flex flex-col sm:max-w-md">
        <DrawerHeader className="flex flex-row items-center justify-between border-b pb-3">
          <DrawerTitle className="flex items-center gap-2 text-base">
            <ShoppingCartIcon className="h-4 w-4" />
            Koszyk
            {totalQuantity > 0 && (
              <span className="bg-primary text-primary-foreground inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold">
                {totalQuantity}
              </span>
            )}
          </DrawerTitle>
          <DrawerClose asChild>
            <button
              aria-label="Zamknij koszyk"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 pt-4">
          <div>
            <div>
              {isLoading && finalCartItems.length === 0 ? (
                <div className="flex h-40 items-center justify-center">
                  <Spinner />
                </div>
              ) : finalCartItems.length === 0 ? (
                <div className="flex h-40 flex-col items-center justify-center gap-2 text-center">
                  <ShoppingCartIcon className="text-muted-foreground h-10 w-10" />
                  <p className="text-muted-foreground text-sm">
                    Twój koszyk jest pusty
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {finalCartItems.map(({ product, quantity }) => (
                    <CartItemRow
                      key={product.clientProductId}
                      item={product}
                      quantity={quantity}
                      isCompany={isCompany}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2 border-t border-b py-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">
                  Subskrypcje ({totalQuantity} szt.)
                </span>
                <span className="text-muted-foreground text-sm font-semibold">
                  {displayPrice.toFixed(2)} zł {isCompany ? "netto" : "brutto"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">VAT</span>
                <span className="text-muted-foreground text-sm font-semibold">
                  {totalVat.toFixed(2)} zł
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-sm">Dostawa</span>
                <span className="text-sm font-semibold text-emerald-500">
                  Gratis
                </span>
              </div>
            </div>
            <div className="space-y-4 py-4">
              <h3 className="flex items-center justify-between text-lg font-bold">
                Razem / miesiąc{" "}
                <span>
                  {displayPrice.toFixed(2)} zł
                  {isCompany ? " netto" : " brutto"}
                </span>
              </h3>
              {isCompany && (
                <p className="text-muted-foreground text-xs">
                  {totalBrutto.toFixed(2)} zł brutto
                </p>
              )}
              {!isCompany && (
                <p className="text-muted-foreground text-xs">
                  {totalNetto.toFixed(2)} zł netto
                </p>
              )}
              <div className="flex items-center justify-between rounded-lg border border-black p-4">
                <span className="flex items-center gap-2 text-xs font-semibold">
                  <SafeUpIcon /> Safe Up
                </span>
                <span className="text-xs font-semibold">w cenie</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">Typ klienta:</span>
                <ClientTypeSwitch
                  isCompany={isCompany}
                  onToggle={setIsCompany}
                />
              </div>
              <PromoCodeBanner />
              <div className="space-y-2">
                {checkoutError && (
                  <p className="text-destructive text-center text-xs">
                    {checkoutError}
                  </p>
                )}
                <Button
                  variant="blue"
                  className="w-full"
                  disabled={finalCartItems.length === 0 || isCheckingOut}
                  onClick={handleCheckout}
                >
                  {isCheckingOut ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <CreditCardIcon />
                  )}{" "}
                  Przejdź do płatności
                </Button>
                <Button
                  variant={"ghost"}
                  className="text-muted-foreground w-full"
                  onClick={closeCart}
                >
                  <ArrowLeftIcon /> Kontynuuj zakupy
                </Button>
              </div>
              <div className="text-muted-foreground rounded-xl border p-4 text-center text-xs">
                Comiesięczna faktura VAT · Wymiana sprzętu po 6 mies. · Ochrona
                Safe Up w cenie
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
